import LZString from 'lz-string';

import { FilterOperator } from '__generated__/globalTypes';
import {
  BaseFiltersConfig,
  QueryFilter,
  RangeFilter,
  DateRangeFilter,
  FilterValue,
  RangeFilterConfigEntry,
  DateRangeFilterConfigEntry,
} from 'ui-kit/PillFilters/filters.types';

export function filterValuesToSearchQuery<T extends BaseFiltersConfig>(
  filters: FilterValue<T>[],
  filterFieldToAbbr: Record<keyof T, string>,
): string {
  return filters
    .map((filter) => {
      const abbr = filterFieldToAbbr[filter.name];
      if ('min' in filter && 'max' in filter) {
        // Range filter
        return `${abbr}|r|${filter.min}|${filter.max}`;
      } else if ('values' in filter && Array.isArray(filter.values)) {
        // List-input filter
        const idsString = filter.values.join(',');
        const compressedIds = LZString.compressToEncodedURIComponent(idsString);
        return `${abbr}|l|${compressedIds}`;
      } else if ('value' in filter) {
        const value = filter.value;
        if (Array.isArray(value)) {
          // Multi-select filter
          const encodedValues = value.map(encodeURIComponent).join('|');
          return `${abbr}|m|${encodedValues}`;
        } else if (typeof value === 'boolean') {
          // Boolean filter
          return `${abbr}|b|${value}`;
        } else if (value != null) {
          // Single value filter
          return `${abbr}|v|${encodeURIComponent(value.toString())}`;
        } else {
          return `${abbr}|v|`;
        }
      } else {
        throw new Error(`Invalid filter structure for filter "${String(filter)}"`);
      }
    })
    .join(';');
}

export function searchQueryToFilterValues<T extends BaseFiltersConfig>(
  searchQuery: string,
  abbrToFilterField: Record<string, keyof T>,
): FilterValue<T>[] {
  if (!searchQuery) return [];
  const filtersStr = searchQuery.split(';');
  return filtersStr
    .map((filterStr): FilterValue<T> | null => {
      const parts = filterStr.split('|');
      const [abbr, typeIdentifier, ...valueParts] = parts;
      const name = abbrToFilterField[abbr];

      if (!name) {
        return null;
      }

      switch (typeIdentifier) {
        case 'r': {
          // Range filter
          if (valueParts.length !== 2) {
            throw new Error(`Invalid range filter format for "${abbr}"`);
          }
          const [minStr, maxStr] = valueParts;
          const min = Number(minStr);
          const max = Number(maxStr);
          if (isNaN(min) || isNaN(max)) {
            throw new Error(`Invalid number in range filter for "${abbr}"`);
          }
          return {
            name,
            min,
            max,
          };
        }
        case 'v': {
          // Single value filter
          if (valueParts.length !== 1) {
            throw new Error(`Invalid value filter format for "${abbr}"`);
          }
          const value = decodeURIComponent(valueParts[0]);
          return {
            name,
            value,
          };
        }
        case 'm': {
          // Multi-select filter
          if (valueParts.length === 1 && valueParts[0] === '') {
            // Empty multi-select
            return {
              name,
              value: [],
            };
          }
          const values = valueParts.map(decodeURIComponent);
          return {
            name,
            value: values,
          };
        }
        case 'b': {
          // Boolean filter
          if (valueParts.length !== 1) {
            throw new Error(`Invalid boolean filter format for "${abbr}"`);
          }
          const valueStr = valueParts[0];
          if (valueStr !== 'true' && valueStr !== 'false') {
            throw new Error(`Invalid boolean value "${valueStr}" for filter "${abbr}"`);
          }
          return {
            name,
            value: valueStr === 'true',
          };
        }
        case 'l': {
          // List-input filter
          if (valueParts.length !== 1) {
            throw new Error(`Invalid list-input filter format for "${abbr}"`);
          }
          const compressedIds = valueParts[0];
          const idsString = LZString.decompressFromEncodedURIComponent(compressedIds);
          if (idsString === null) {
            throw new Error(`Failed to decompress list-input filter for "${abbr}"`);
          }
          const ids = idsString.split(',').filter((id) => id.length > 0);
          return {
            name,
            values: ids,
          };
        }
        default:
          throw new Error(
            `Unknown filter type identifier "${typeIdentifier}" for filter "${abbr}"`,
          );
      }
    })
    .filter((filter): filter is FilterValue<T> => filter !== null);
}

export function filtersToQueryFilters<T extends BaseFiltersConfig>(
  valueFilters: FilterValue<T>[],
  filtersConfig: T,
): QueryFilter<T>[] {
  return valueFilters
    .map((filter) => {
      const config = filtersConfig[filter.name];
      // A filter's name can outlive its config entry — e.g. a stale
      // abbreviation in filterFieldToAbbr still decodes via
      // abbrToFilterField (searchQueryToFilterValues) to a name no longer
      // present in filtersConfig, whenever a field gets dropped from one
      // without the other being updated to match. Skip it here the same
      // way PillFilters.tsx already does for its own render-path lookup,
      // rather than crashing every page that calls filtersToQueryFilters
      // for anyone loading a URL carrying that abbreviation.
      if (!config) {
        return [];
      }
      if (config.type === 'range' && 'min' in filter) {
        return convertRangeFilter(config, filter);
      } else if (config.type === 'date-range' && 'min' in filter) {
        return convertDateRangeFilter(config, filter);
      } else if (config.type === 'list-input' && 'values' in filter) {
        return filter.values.length
          ? [
              {
                field_name: filter.name,
                operator: FilterOperator.IS,
                operandList: filter.values,
              },
            ]
          : [];
      } else if ('value' in filter && Array.isArray(filter.value)) {
        return filter.value.length > 0
          ? {
              field_name: filter.name,
              operator: FilterOperator.IS,
              operandList: filter.value,
            }
          : [];
      } else if ('value' in filter) {
        return filter.value !== null
          ? {
              field_name: filter.name,
              operator: FilterOperator.IS,
              operand: filter.value.toString(),
            }
          : [];
      } else {
        throw new Error('Invalid filter');
      }
    })
    .flat();
}

function convertRangeFilter<T extends BaseFiltersConfig>(
  configEntry: RangeFilterConfigEntry,
  filter: RangeFilter<T>,
) {
  // Parity with convertDateRangeFilter: an untouched range (still at the config
  // default bounds) emits no clause, so merely adding the pill does not exclude every
  // loan on fields the backend cannot serve (ENG-1837 silent-exclusion class).
  if (filter.min === configEntry.min && filter.max === configEntry.max) {
    return [];
  }
  const prepare = configEntry.prepare ? configEntry.prepare : (value: number) => value;
  return [
    {
      field_name: filter.name,
      operator: FilterOperator.LESS_THAN_OR_EQUALS,
      operand: prepare(filter.max).toString(),
    },
    {
      field_name: filter.name,
      operator: FilterOperator.GREATER_THAN_OR_EQUALS,
      operand: prepare(filter.min).toString(),
    },
  ];
}

function convertDateRangeFilter<T extends BaseFiltersConfig>(
  configEntry: DateRangeFilterConfigEntry,
  filter: DateRangeFilter<T>,
) {
  if (filter.min === configEntry.min && filter.max === configEntry.max) {
    return [];
  }
  return [
    {
      field_name: filter.name,
      operator: FilterOperator.LESS_THAN_OR_EQUALS,
      operand: Math.floor(filter.max).toString(),
    },
    {
      field_name: filter.name,
      operator: FilterOperator.GREATER_THAN_OR_EQUALS,
      operand: Math.floor(filter.min).toString(),
    },
  ];
}
